import { useCallback, useMemo } from "react";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { formatNumber } from "src/utils/numberFormat";
import { useCreateListLabel } from "../CreateList/useCreateListLabel";
import { CREATE_SEGMENT_KEY, LIST_OPTIONS_KEY, PREDICTED_LISTS } from "./CreateSegment.Constants";

const DATE_FORMAT_OPTIONS: Intl.DateTimeFormatOptions = { month: "short", day: "numeric", year: "numeric" };

export const useSegmentLabel = () => useCreateListLabel(CREATE_SEGMENT_KEY);

export const useSegmentOptions = <T extends string>(path: string, values: readonly T[]) => {
  const label = useSegmentLabel();
  return useMemo(
    () => values.map(value => ({ value, label: label(`${path}.${value}`) })),
    [label, path, values]
  );
};

export const usePredictedListLabel = () => {
  const { t } = useTranslation();
  return useCallback(
    (listName: string) => {
      const list = PREDICTED_LISTS.find(item => item.value === listName);
      return list ? t(`${LIST_OPTIONS_KEY}.${list.key}`) : listName;
    },
    [t]
  );
};

export const useSegmentFormatters = () => {
  const { i18n } = useTranslation();
  const language = i18n.language;

  return useMemo(() => {
    const dateFormatter = new Intl.DateTimeFormat(language, DATE_FORMAT_OPTIONS);
    const listFormatter = new Intl.ListFormat(language, { type: "disjunction" });
    return {
      formatValue: (value: number): string => formatNumber(value, {}, language),
      formatDate: (value: string) => dateFormatter.format(dayjs(value).toDate()),
      formatList: (values: string[]) => listFormatter.format(values),
    };
  }, [language]);
};
